import { googleLanguageFor } from '../google-ai/localePolicy'
import { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usePortalUiLanguage } from '../contexts/PortalUiLanguageContext'
import {
  buildEmployeeCabinetNav,
  type CabinetNavItem,
  cabinetNavHrefIsActive,
  employeeCabinetPositionTitle,
} from '../utils/employeeCabinetNav'
import {
  GOOGLE_DESK_TASKS_CHANGED_EVENT,
  localCalendarYmd,
} from '../utils/googleDeskTodayRemainingCount'
import EmployeeWorkspaceActions from './EmployeeWorkspaceActions'
import { adminNavTo } from '../utils/mailAlertHost'
import '../../../api/static/zam_portal_nav.css'
import './EmployeeCabinetShell.css'
import '../../../api/static/employee_portal_theme.css'
import '../../../api/static/employee_workspace.css'

function tr(lang: 'ru' | 'en' | 'id', ru: string, en: string, id: string): string {
  return lang === 'ru' ? ru : (lang === 'id' ? id : en)
}
function pathnameOnly(navTo: string): string {
  const t = String(navTo || '').trim()
  if (!t) return ''
  if (t.startsWith('http://') || t.startsWith('https://')) {
    try {
      return new URL(t).pathname
    } catch {
      return t
    }
  }
  return t.split('?')[0] || t
}

export default function EmployeeCabinetShell({ children }: { children: React.ReactNode }) {
  const { user, canAccessFleetAiAnalytics, canAccessTgMonitoring, canAccessCreativePortal } = useAuth()
  const { lang: preferredLang, setLang } = usePortalUiLanguage()
  const location = useLocation()
  const lang = /^\/google-ai(?:\/|$)/.test(location.pathname) ? googleLanguageFor(user, preferredLang) : preferredLang
  const [navOpen, setNavOpen] = useState(false)
  const [tb2OpenCount, setTb2OpenCount] = useState(0)
  /** Карточки КреоПортала (полный список календаря) со live score строго ниже 60 — зона задач 2.0. */
  const [crcCriticalBelow60, setCrcCriticalBelow60] = useState(0)
  const [controlZoneCounts, setControlZoneCounts] = useState<Record<string, number>>({})
  const [deskTodayCount, setDeskTodayCount] = useState(0)
  const deskTodayInFlight = useRef(false)
  const deskTodayLoadedAt = useRef(0)
  const normPos = String(user?.normalized_position || '').trim()
  const isGoogleDeskSpecialist = normPos === 'Google'


  useEffect(() => {
    if (user?.role !== 'employee') return
    // React owns every visible string in this shell. Keep the selected language
    // available to server-rendered pages without letting the legacy DOM
    // translator rewrite React text nodes into a mixed-language state.
    try {
      localStorage.setItem('portal_language', lang)
    } catch {
      /* private mode */
    }
    document.cookie = `lang=${lang}; path=/; max-age=31536000; SameSite=Lax`
    document.cookie = `portal_lang=${lang}; path=/; max-age=31536000; SameSite=Lax`
  }, [lang, user?.role])
  const selectLanguage = useCallback((next: 'ru' | 'en' | 'id') => {
    setLang(next)
    if (user?.role !== 'employee') return
    void fetch('/api/employee/settings/language', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ language: next }),
    }).catch(() => {})
  }, [setLang, user?.role])

  useEffect(() => {
    if (user?.role !== 'employee' || (user?.permissions && !user.permissions.includes('task.view'))) {
      setTb2OpenCount(0)
      return
    }
    let cancelled = false
    const refreshCount = async () => {
      if (document.hidden) return
      try {
        const r = await fetch('/api/employee/task-board-v2/open-count', { credentials: 'include' })
        if (!r.ok) return
        const j = (await r.json().catch(() => ({}))) as { count?: number }
        const n = Math.max(0, Math.floor(Number(j.count) || 0))
        if (!cancelled) setTb2OpenCount(n)
      } catch {
        /* ignore */
      }
    }
    void refreshCount()
    const refresh = () => { void refreshCount() }
    window.addEventListener("focus", refresh)
    window.addEventListener("employee-tasks-changed", refresh)
    const timer = window.setInterval(refresh, 60000)
    return () => {
      cancelled = true
      clearInterval(timer)
      window.removeEventListener("focus", refresh)
      window.removeEventListener("employee-tasks-changed", refresh)
    }
  }, [user?.role, user?.employee_id, user?.id, user?.permissions, location.pathname])

  useEffect(() => {
    if (user?.role !== 'employee' || normPos !== 'Zam' || !canAccessCreativePortal) {
      setCrcCriticalBelow60(0)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const r = await fetch('/api/employee/zam/nav-counts', { credentials: 'include' })
        if (!r.ok) return
        const j = (await r.json().catch(() => ({}))) as {
          counts?: { creative_portal?: number }
        }
        const n = Math.max(0, Math.floor(Number(j.counts?.creative_portal) || 0))
        if (!cancelled) setCrcCriticalBelow60(n)
      } catch {
        if (!cancelled) setCrcCriticalBelow60(0)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [user?.role, user?.employee_id, user?.id, normPos, canAccessCreativePortal])

  useEffect(() => {
    if (user?.role !== 'employee' || normPos !== 'Control') {
      setControlZoneCounts({})
      return
    }
    let cancelled = false
    ;(async () => {
      // Navigation badges must never build full Google/Meta/creative/aggregator
      // dashboards. Those read models belong to their operational pages.
      let payload: { risk_counts?: { google?: number; facebook?: number } } | null = null
      try {
        const response = await fetch('/api/employee/redzone', { credentials: 'include', cache: 'default' })
        payload = response.ok ? await response.json() : null
      } catch {
        payload = null
      }
      if (cancelled) return
      const counts = payload?.risk_counts || {}
      setControlZoneCounts({
        google_zone: Math.max(0, Math.floor(Number(counts.google) || 0)),
        meta_zone: Math.max(0, Math.floor(Number(counts.facebook) || 0)),
      })
    })()
    return () => { cancelled = true }
  }, [user?.role, user?.employee_id, user?.id, normPos])
  const loadDeskTodayCount = useCallback(async () => {
    if (user?.role !== 'employee' || !isGoogleDeskSpecialist) {
      setDeskTodayCount(0)
      return
    }
    const eidLocal = String(user?.employee_id || user?.id || '').trim()
    if (!eidLocal) {
      setDeskTodayCount(0)
      return
    }
    const now = Date.now()
    if (deskTodayInFlight.current || now - deskTodayLoadedAt.current < 15_000) return
    deskTodayInFlight.current = true
    try {
      const todayYmd = localCalendarYmd()
      const tb2Res = await fetch(
        `/api/employee/task-board-v2/today?day=${encodeURIComponent(todayYmd)}`,
        { credentials: 'include' },
      )
      if (tb2Res.ok) {
        const tj = (await tb2Res.json().catch(() => ({}))) as { count?: number }
        setDeskTodayCount(Math.max(0, Math.floor(Number(tj.count) || 0)))
      } else {
        setDeskTodayCount(0)
      }
    } catch {
      setDeskTodayCount(0)
    } finally {
      deskTodayInFlight.current = false
      deskTodayLoadedAt.current = Date.now()
    }
  }, [user?.role, user?.employee_id, user?.id, isGoogleDeskSpecialist])

  useEffect(() => {
    void loadDeskTodayCount()
  }, [loadDeskTodayCount, location.pathname])

  useEffect(() => {
    if (!isGoogleDeskSpecialist || user?.role !== 'employee') return
    const onVis = () => {
      if (document.visibilityState === 'visible') void loadDeskTodayCount()
    }
    const onDeskTasksChanged = () => void loadDeskTodayCount()
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener(GOOGLE_DESK_TASKS_CHANGED_EVENT, onDeskTasksChanged)
    return () => {
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener(GOOGLE_DESK_TASKS_CHANGED_EVENT, onDeskTasksChanged)
    }
  }, [isGoogleDeskSpecialist, user?.role, loadDeskTodayCount])

  /** Периодически: после «Готово» во iframe главной вкладка может не получить visibilitychange. */
  useEffect(() => {
    if (!isGoogleDeskSpecialist || user?.role !== 'employee') return
    const id = window.setInterval(() => void loadDeskTodayCount(), 90_000)
    return () => window.clearInterval(id)
  }, [isGoogleDeskSpecialist, user?.role, loadDeskTodayCount])

  useEffect(() => {
    setNavOpen(false)
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.pathname])

  useEffect(() => {
    if (!navOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setNavOpen(false)
        document.querySelector<HTMLButtonElement>('.emp-cab__nav-toggle')?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [navOpen])

  const eid = String(user?.employee_id || user?.id || '').trim()
  const nav = buildEmployeeCabinetNav({
    employeeId: eid,
    normalizedPosition: user?.normalized_position,
    workspace: user?.workspace,
    normalizedPositions: user?.normalized_positions,
    deskPrefs: user?.desk_prefs ?? null,
    canAccessFleetAiAnalytics,
    canAccessTgMonitoring,
    taskBoardV2Assigner: Boolean(user?.task_board_v2_assigner),
    permissions: user?.permissions ?? null,
    lang,
    adminSpaHome: user?.role === 'admin',
  })

  const roleTitle = (user?.normalized_positions?.length ? user.normalized_positions : [user?.normalized_position])
    .filter((value): value is string => Boolean(value))
    .map((value) => employeeCabinetPositionTitle(lang, value))
    .join(' · ') || employeeCabinetPositionTitle(lang, user?.normalized_position)
  const displayName = user?.name || tr(lang, 'Сотрудник', 'Employee', 'Karyawan')
  const dn = displayName.trim()
  const avatarInitial = dn ? dn.charAt(0).toUpperCase() : '?'

  const homeItem = nav.find((x) => x.id === 'home')
  const mainNav = nav.filter((item) => item.id !== 'settings')
  const settingsHref = adminNavTo('/employee/settings')
  const settingsActive = cabinetNavHrefIsActive(
    settingsHref,
    location.pathname,
    location.search,
  )
  const homePath = homeItem ? pathnameOnly(homeItem.to || homeItem.href || '') : ''
  /** Под «Главная» для /employee/desk/... подсвечиваем и вложенные маршруты SPA */
  const homeNavLinkEnd = !homePath.startsWith('/employee/desk/')

  const logoutHref = adminNavTo('/logout')
  const activeItem = [...nav].reverse().find((item) =>
    !item.external && cabinetNavHrefIsActive(item.to || item.href || '', location.pathname, location.search),
  )
  const metaHomeWorkspace = location.pathname === '/employee/home' && normPos === 'Facebook'
  const designerHomeWorkspace = location.pathname === '/employee/home' && normPos === 'Designer'
  const zamHomeWorkspace = location.pathname === '/employee/home' && normPos === 'Zam'
  const controlHomeWorkspace = location.pathname === '/employee/home' && normPos === 'Control'
  const isWorkspaceHome = location.pathname === '/employee/home'
  const workspace = user?.workspace
  const isPrimaryItem = (id: string) => id === 'home' || workspace?.actions.some(action => action.id === id)
  const isTeamItem = (id: string) => id === 'slack' || id === 'telegram_bot'
  const primaryNav = workspace ? mainNav.filter(item => isPrimaryItem(item.id)) : mainNav
  const toolsNav = workspace ? mainNav.filter(item => !isPrimaryItem(item.id) && !isTeamItem(item.id)) : []
  const teamNav = workspace ? mainNav.filter(item => isTeamItem(item.id)) : []
  const pageTitle = isWorkspaceHome && workspace ? workspace.title[lang] : metaHomeWorkspace
      ? tr(lang, 'Meta Ads · Сегодня', 'Meta Ads · Today', 'Meta Ads · Hari ini')
    : activeItem?.label || tr(lang, 'Рабочий кабинет', 'Workspace', 'Ruang kerja')
  const isSettingsPage = location.pathname === '/employee/settings' || location.pathname === '/assistant/settings'
  const wideWorkspace = controlHomeWorkspace || zamHomeWorkspace || metaHomeWorkspace || designerHomeWorkspace || /\/(slack|tasks|task-2-0|creative-calendar|monitoring_tg|system-ai-analytics)(\/|$)/.test(location.pathname)
  const taskWorkspace = /^\/(employee|assistant)\/(tasks|task-2-0)(\/|$)/.test(location.pathname)
  const pageDescription = isWorkspaceHome && workspace ? workspace.focus[lang] : metaHomeWorkspace
      ? tr(lang, 'Задачи, проблемные проекты и действия по Meta — в одном рабочем экране.', 'Meta tasks, at-risk projects and next actions in one workspace.', 'Tugas Meta, proyek bermasalah, dan tindakan berikutnya dalam satu layar kerja.')
    : activeItem?.id === 'home'
      ? tr(lang, 'Ваши задачи, клиенты и рабочие инструменты.', 'Your tasks, clients and work tools in one place.', 'Tugas, klien, dan alat kerja Anda dalam satu tempat.')
    : activeItem?.id === 'slack'
      ? tr(lang, 'Обсуждения команды и проектов в одном месте.', 'Team and project conversations in one place.', 'Percakapan tim dan proyek dalam satu tempat.')
      : tr(lang, 'Всё для работы в этом разделе.', 'Everything you need for this part of your work.', 'Semua yang Anda perlukan untuk bagian pekerjaan ini.')

  const renderNavItem = (item: CabinetNavItem) => {
            const controlZoneCount = Number(controlZoneCounts[item.id] || 0)
            const navLabel =
              item.id === 'telegram_bot' ? (
                <>{item.label}<span data-tg-bot-unread="" hidden /></>
              ) :
              controlZoneCount > 0 ? (
                <>{item.label} <span className="emp-cab__nav-badge">{controlZoneCount > 99 ? '99+' : controlZoneCount}</span></>
              ) :
              item.id === 'home' && isGoogleDeskSpecialist && deskTodayCount > 0 ? (
                <>
                  {item.label}{' '}
                  <span
                    className="emp-cab__nav-badge"
                    aria-label={
                      lang === 'id' ? 'Tugas terbuka hari ini: ' + deskTodayCount : lang === 'en'
                        ? `Open tasks due today: ${deskTodayCount}`
                        : `Открыто на сегодня: ${deskTodayCount}`
                    }
                  >
                    {deskTodayCount > 99 ? '99+' : deskTodayCount}
                  </span>
                </>
              ) : item.id === 'tasks' && tb2OpenCount > 0 ? (
                <>
                  {item.label}{' '}
                  <span
                    className="emp-cab__nav-badge"
                    aria-label={
                      lang === 'id' ? 'Tugas 2.0 terbuka: ' + tb2OpenCount : lang === 'en' ? 'Open Tasks 2.0: ' + tb2OpenCount : 'Открытых задач 2.0: ' + tb2OpenCount
                    }
                  >
                    {tb2OpenCount}
                  </span>
                </>
              ) : item.id === 'creative_calendar' && crcCriticalBelow60 > 0 ? (
                <>
                  {item.label}{' '}
                  <span
                    className="emp-cab__nav-badge"
                    aria-label={
                      lang === 'id' ? 'Tugas kreatif terbuka: ' + crcCriticalBelow60 : lang === 'en'
                        ? `Open creative tasks: ${crcCriticalBelow60}`
                        : `Карточек со score ниже 60: ${crcCriticalBelow60}`
                    }
                  >
                    {crcCriticalBelow60 > 99 ? '99+' : crcCriticalBelow60}
                  </span>
                </>
              ) : (
                item.label
              )
            if (item.to) {
              return (
                <NavLink
                  key={item.id}
                  to={item.to}
                  end={item.id === 'home' ? homeNavLinkEnd : undefined}
                  className={({ isActive }) => (isActive ? 'active' : undefined)}
                >
                  {navLabel}
                </NavLink>
              )
            }
            if (item.href) {
              const ext = Boolean(item.external)
              const active =
                !ext &&
                cabinetNavHrefIsActive(item.href, location.pathname, location.search)
              const cls = [active ? 'active' : '', ext ? 'zam-nav--external' : ''].filter(Boolean).join(' ')
              return (
                <a key={item.id} className={cls || undefined} href={item.href}>
                  {navLabel}
                </a>
              )
            }
            return null
  }


  return (
    <div className={`emp-cab${navOpen ? ' emp-cab--nav-open' : ''}`}>
      <button
        type="button"
        className="emp-cab__nav-toggle"
        aria-expanded={navOpen}
        aria-controls="emp-cab-aside"
        onClick={() => setNavOpen((v) => !v)}
      >
        {tr(lang, 'Меню', 'Menu', 'Menu')}
      </button>
      {navOpen ? (
        <button
          type="button"
          className="emp-cab__backdrop"
          aria-label={tr(lang, 'Закрыть меню', 'Close menu', 'Tutup menu')}
          onClick={() => setNavOpen(false)}
        />
      ) : null}

      <aside id="emp-cab-aside" className="zam-side emp-cab__aside">
        <div className="zam-brand">ALEX‑DIGITAL</div>

        <div className="zam-profile">
          <div className="zam-avatar" aria-hidden>
            {avatarInitial}
          </div>
          <div>
            <div className="zam-profile-name">{displayName}</div>
            <div className="zam-profile-role">{roleTitle}</div>
          </div>
        </div>

        <nav className="zam-nav" aria-label={tr(lang, 'Разделы кабинета', 'Desk sections', 'Bagian portal')}>
          {primaryNav.map(renderNavItem)}
          {toolsNav.length > 0 && <details className="employee-nav-tools" open={toolsNav.some(item => item.id === activeItem?.id)}>
            <summary>{tr(lang, 'Рабочие инструменты', 'Work tools', 'Alat kerja')}<span>{toolsNav.length}</span></summary>
            {toolsNav.map(renderNavItem)}
          </details>}
          {teamNav.length > 0 && <div className="employee-nav-team">{teamNav.map(renderNavItem)}</div>}
        </nav>

        <a
          className={`emp-cab__profile-settings${settingsActive ? ' active' : ''}`}
          href={settingsHref}
          aria-current={settingsActive ? 'page' : undefined}
        >
          <span aria-hidden="true">⚙</span>
          <span>{tr(lang, 'Настройки профиля', 'Profile settings', 'Pengaturan profil')}</span>
        </a>

        <div className="emp-cab__lang" aria-label={tr(lang, 'Язык интерфейса', 'Interface language', 'Bahasa antarmuka')}>
          <span className="emp-cab__lang-label">{tr(lang, 'Язык', 'Language', 'Bahasa')}</span>
          <div className="emp-cab__lang-btns">
            <button type="button" className={lang === 'ru' ? 'is-on' : undefined} onClick={() => selectLanguage('ru')}>
              RU
            </button>
            <button type="button" className={lang === 'en' ? 'is-on' : undefined} onClick={() => selectLanguage('en')}>
              EN
            </button>
            <button type="button" className={lang === 'id' ? 'is-on' : undefined} onClick={() => selectLanguage('id')}>
              ID
            </button>
          </div>
        </div>

        <a className="zam-logout" href={logoutHref}>
          {tr(lang, 'Выйти', 'Log out', 'Keluar')}
        </a>
      </aside>

      <div className="emp-cab__main">
        {isSettingsPage ? children : (
          <div className={`emp-cab__workspace${wideWorkspace ? ' emp-cab__workspace--wide' : ''}${taskWorkspace ? ' emp-cab__workspace--tasks' : ''}${metaHomeWorkspace ? ' emp-cab__workspace--meta' : ''}${designerHomeWorkspace ? ' emp-cab__workspace--designer' : ''}`}>
            <header className="emp-cab__hero">
              <div>
                <span className="emp-cab__eyebrow">{tr(lang, 'Кабинет сотрудника', 'Employee account', 'Akun karyawan')}</span>
                <h1>{pageTitle}</h1>
                <p>{pageDescription}</p>
              </div>
              <div className="emp-cab__hero-profile"><strong>{displayName}</strong><small>{roleTitle}</small></div>
            </header>
            {isWorkspaceHome && workspace ? <EmployeeWorkspaceActions workspace={workspace} /> : null}
            <div className="emp-cab__content">{children}</div>
          </div>
        )}
      </div>
    </div>
  )
}
